/**
 * Sistema de precios Nuovocell
 * ----------------------------
 * Fórmula resumida del Excel de Nuovocell:
 *
 *   factor_cashea = costo < umbral ? margenCasheaBajo : margenCasheaAlto
 *   factor_krece  = margenKrece
 *
 *   precio_cashea_USD = costo * (1 + factor_cashea) * (tasaInterna / tasaBCV)
 *   precio_krece_USD  = costo * (1 + factor_krece)  * (tasaInterna / tasaBCV)
 *
 * Valores por defecto del equipo Nuovocell:
 *   margenCasheaBajo = 0.44   (44% si costo < 100)
 *   margenCasheaAlto = 0.34   (34% si costo ≥ 100)
 *   umbralCashea     = 100    (USD)
 *   margenKrece      = 0.20   (20% siempre)
 *
 * Validación: probado contra fila 12 del xlsx (costo=$85.95, INTERNA=750, BCV=517.96)
 *   → Cashea $179.21, Krece $149.34. Coincide al céntimo con el Excel.
 */

export const DEFAULTS = {
  margenCasheaBajo: 0.44,
  margenCasheaAlto: 0.34,
  umbralCashea:     100,
  margenKrece:      0.20,
};

/**
 * Calcula los precios derivados de un producto con costo en USD.
 *
 * @param {Object} producto - { costoUSD: number, precioFijo?: number }
 * @param {Object} config   - { tasaCambio, tasaInterna, margenes... }
 * @returns {Object|null}   - { casheaUSD, kreceUSD, casheaBs, kreceBs, divisasUSD } o null
 */
export function calcularPrecios(producto, config) {
  if (!producto || !config) return null;

  const costoUSD = Number(producto.costoUSD);
  const bcv      = Number(config.tasaCambio);
  const interna  = Number(config.tasaInterna);

  // Sin costo o sin tasas no hay cálculo
  if (!isFinite(costoUSD) || costoUSD <= 0) return null;
  if (!isFinite(bcv) || bcv <= 0)           return null;
  if (!isFinite(interna) || interna <= 0)   return null;

  const margenCasheaBajo = num(config.margenCasheaBajo, DEFAULTS.margenCasheaBajo);
  const margenCasheaAlto = num(config.margenCasheaAlto, DEFAULTS.margenCasheaAlto);
  const umbralCashea     = num(config.umbralCashea,     DEFAULTS.umbralCashea);
  const margenKrece      = num(config.margenKrece,      DEFAULTS.margenKrece);

  const factorAjuste = interna / bcv;
  const margenCashea = costoUSD < umbralCashea ? margenCasheaBajo : margenCasheaAlto;

  const casheaUSD = costoUSD * (1 + margenCashea) * factorAjuste;
  const kreceUSD  = costoUSD * (1 + margenKrece)  * factorAjuste;

  return {
    casheaUSD: round2(casheaUSD),
    kreceUSD:  round2(kreceUSD),
    casheaBs:  round2(casheaUSD * bcv),
    kreceBs:   round2(kreceUSD  * bcv),
    divisasUSD: round2(costoUSD + 18), // precio mostrador (costo + $18)
    factorAjuste,
    margenAplicado: margenCashea,
  };
}

/**
 * Devuelve el precio final mostrado al cliente. Cashea es el principal.
 *
 * Reglas de resolución (en orden de prioridad):
 *   1. Si el producto tiene `costoUSD` → aplicar fórmula (recalcula dinámicamente con la tasa actual)
 *   2. Si no, usar `precio` como precio fijo manual (productos sin costo cargado: laptops, iPhones, etc.)
 *   3. Si no tiene ninguno → null (la UI muestra "Consultar precio")
 *
 * Esto permite convivencia: productos del Excel usan fórmula, productos manuales mantienen precio fijo.
 */
export function precioVenta(producto, config) {
  if (!producto) return null;

  // Prioridad 1: cálculo dinámico con costoUSD
  if (isFinite(Number(producto.costoUSD)) && Number(producto.costoUSD) > 0) {
    const p = calcularPrecios(producto, config);
    if (p) return p.casheaUSD;
  }

  // Prioridad 2: precio manual fijo (productos sin costo cargado)
  if (isFinite(Number(producto.precio)) && Number(producto.precio) > 0) {
    return Number(producto.precio);
  }

  return null;
}

/**
 * Indica si el precio mostrado proviene de la fórmula (true) o es manual (false).
 * Útil para UI debugging y para el admin.
 */
export function esPrecioCalculado(producto) {
  return !!(producto && isFinite(Number(producto.costoUSD)) && Number(producto.costoUSD) > 0);
}

// ============== utils internos ==============
function num(v, fallback) {
  const n = Number(v);
  return isFinite(n) ? n : fallback;
}
function round2(n) {
  return Math.round(n * 100) / 100;
}
